import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * Selector matching the first focusable element within a newly loaded item.
 * Used to move focus to freshly loaded content for keyboard and screen reader users.
 * @constant {string}
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [loadMoreProgress] - The load-more progress bar fill element.
 * @property {HTMLElement} [loadMoreText] - The load-more text element.
 * @property {HTMLElement} [loadMoreCount] - The load-more count element.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLAnchorElement} [seoPaginationPrev] - The SEO pagination prev link.
 * @property {HTMLAnchorElement} [seoPaginationNext] - The SEO pagination next link.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {WeakSet<Element>} */
  #loadMoreButtonsWithListeners = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');

    if (this.#isLoadMore) {
      this.#addLoadMoreListeners();
    } else if (this.#isInfiniteScroll) {
      this.#observeViewMore();
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Whether the component is in load-more (button) mode.
   * @returns {boolean}
   */
  get #isLoadMore() {
    return this.getAttribute('pagination-type') === 'load_more';
  }

  /**
   * Whether the component is in infinite-scroll (auto) mode.
   * @returns {boolean}
   */
  get #isInfiniteScroll() {
    return this.getAttribute('pagination-type') === 'infinite_scroll';
  }

  #addLoadMoreListeners() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    this.#attachLoadMoreListener(viewMorePrevious, () => this.#renderPreviousPage());
    this.#attachLoadMoreListener(viewMoreNext, () => this.#renderNextPage());
  }

  /**
   * Attaches a click listener to the load-more buttons inside a container.
   *
   * The listener is bound to the buttons themselves rather than the wrapping
   * container so that screen readers do not announce the non-interactive
   * container as an additional "clickable" control.
   *
   * @param {HTMLElement | undefined} container - The container holding the buttons.
   * @param {() => void} handler - The click handler.
   */
  #attachLoadMoreListener(container, handler) {
    if (!container) return;

    container.querySelectorAll('button.load-more-button').forEach((button) => {
      if (this.#loadMoreButtonsWithListeners.has(button)) return;

      this.#loadMoreButtonsWithListeners.add(button);
      button.addEventListener('click', handler);
    });
  }

  #observeViewMore() {
    // Don't create IntersectionObserver for load_more mode
    if (this.#isLoadMore) return;

    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        },
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    const firstNewItem = nextPageItemElements[0];

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    await yieldToMainThread();

    // Only update URL for load_more (explicit user action), not for auto-scroll
    if (this.#isLoadMore) {
      this.#updateLoadMoreProgress();
      this.#updateSeoPaginationLinks();

      history.pushState('', '', nextPage.url.toString());

      // Move focus to the first newly loaded item so keyboard and screen reader
      // users are informed that new content has been added and can continue
      // tabbing forward through the new products.
      this.#focusFirstNewItem(firstNewItem);

      // Hide load-more button if we've reached the last page
      const nextPageAfterRender = this.#getPage('next');
      const { viewMoreNext } = this.refs;
      if (viewMoreNext && (!nextPageAfterRender || !this.#shouldUsePage(nextPageAfterRender))) {
        viewMoreNext.style.display = 'none';
      }
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * Moves focus to the first focusable element within a newly loaded grid item.
   * This informs keyboard and screen reader users that new content has loaded.
   * @param {Element | undefined} item - The first newly loaded grid item.
   */
  #focusFirstNewItem(item) {
    if (!item) return;

    const focusable = item.matches(FOCUSABLE_SELECTOR) ? item : item.querySelector(FOCUSABLE_SELECTOR);

    if (focusable instanceof HTMLElement) {
      focusable.focus();
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Update URL BEFORE prepending elements so that banner-blocks.js
    // reads the correct page number from the URL when the MutationObserver fires
    if (this.#isLoadMore) {
      history.pushState('', '', previousPage.url.toString());
    }

    // Store the current scroll position and height of the first visible element (skip script tags)
    const currentScrollTop = getScrollTop();
    const firstElement = grid.querySelector(':scope > *:not(script)');
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    // Only update UI for load_more (explicit user action), not for auto-scroll
    if (this.#isLoadMore) {
      this.#updateLoadMoreProgress();
      this.#updateSeoPaginationLinks();

      // Hide load-previous button if we've reached the first page
      const prevPageAfterRender = this.#getPage('previous');
      const { viewMorePrevious } = this.refs;
      if (viewMorePrevious && (!prevPageAfterRender || !this.#shouldUsePage(prevPageAfterRender))) {
        viewMorePrevious.style.display = 'none';
      }
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    // Include all direct children except scripts (to include banner blocks along with product cards)
    return gridElement.querySelectorAll(':scope > *:not(script)');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Re-show load-more buttons when filters change
    if (this.#isLoadMore) {
      const { viewMorePrevious, viewMoreNext } = this.refs;
      if (viewMorePrevious) viewMorePrevious.style.display = '';
      if (viewMoreNext) viewMoreNext.style.display = '';
    }

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        if (this.#isLoadMore) {
          this.#addLoadMoreListeners();
        } else if (this.#isInfiniteScroll) {
          this.#observeViewMore();
        }

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };

  /**
   * Updates the SEO pagination links based on current page state.
   */
  #updateSeoPaginationLinks() {
    const { seoPaginationPrev, seoPaginationNext } = this.refs;

    const prevPage = this.#getPage('previous');
    const nextPage = this.#getPage('next');

    if (seoPaginationPrev) {
      if (prevPage && this.#shouldUsePage(prevPage)) {
        seoPaginationPrev.href = prevPage.url.toString();
        seoPaginationPrev.style.display = '';
      } else {
        seoPaginationPrev.style.display = 'none';
      }
    }

    if (seoPaginationNext) {
      if (nextPage && this.#shouldUsePage(nextPage)) {
        seoPaginationNext.href = nextPage.url.toString();
        seoPaginationNext.style.display = '';
      } else {
        seoPaginationNext.style.display = 'none';
      }
    }
  }

  /**
   * Updates the load-more progress bar and text based on current visible items.
   */
  #updateLoadMoreProgress() {
    const { loadMoreProgress, loadMoreText, loadMoreCount, grid } = this.refs;

    if (!loadMoreProgress || !loadMoreText || !grid) return;

    const totalItems = Number(loadMoreText.dataset.totalItems);
    if (!totalItems) return;

    const currentItems = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
    const percentage = Math.round((currentItems / totalItems) * 100);

    loadMoreProgress.style.width = `${percentage}%`;
    if (loadMoreCount) {
      loadMoreCount.textContent = String(currentItems);
    }
  }
}
